import React, { useEffect, useMemo, useState } from "react";
import { AiOutlineCopy, AiOutlineLoading3Quarters } from "react-icons/ai";
import { useParams } from "react-router-dom";
import { adminServerInstance } from "../../service/axios";
import { handleApiError } from "../../utils/ErrorMessageUtils";
import "./promo-code-public.scss";

const fillPlaceholders = (template, values) => {
  if (!template) {
    return "-";
  }
  return Object.entries(values).reduce(
    (acc, [key, value]) => acc.replace(new RegExp(`\\{${key}\\}`, "g"), String(value)),
    template
  );
};

const copyTextToClipboard = async (text) => {
  if (typeof navigator !== "undefined" && navigator.clipboard?.writeText) {
    await navigator.clipboard.writeText(text);
    return true;
  }

  if (typeof document === "undefined") {
    return false;
  }

  const textarea = document.createElement("textarea");
  textarea.value = text;
  textarea.setAttribute("readonly", "");
  textarea.style.position = "fixed";
  textarea.style.top = "-1000px";
  document.body.appendChild(textarea);
  textarea.select();

  try {
    return document.execCommand("copy");
  } finally {
    document.body.removeChild(textarea);
  }
};

const PromoCodePublic = ({ data }) => {
  const { code = "" } = useParams();
  const [promoData, setPromoData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [errorMsg, setErrorMsg] = useState("");
  const [actionErrorMsg, setActionErrorMsg] = useState("");
  const [successMsg, setSuccessMsg] = useState("");

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    if (!code) {
      setErrorMsg(handleApiError(data, "PROMO_CODE_REQUIRED", data.generalError));
      setLoading(false);
      return;
    }

    setLoading(true);
    setErrorMsg("");
    setActionErrorMsg("");
    setSuccessMsg("");

    adminServerInstance
      .get(`/public/promocode?code=${encodeURIComponent(code)}`, {
        headers: {
          "Content-Type": "application/json",
        },
      })
      .then((response) => {
        setPromoData(response.data);
      })
      .catch((error) => {
        const errorMessage = error?.response?.data?.message;
        setErrorMsg(handleApiError(data, errorMessage, data.generalError));
      })
      .finally(() => {
        setLoading(false);
      });
  }, [code, data]);

  const promo = promoData?.item || promoData?.promo || null;
  const plan = promo?.plan || promoData?.plan || null;

  const maxCount = Number(promo?.max_count || 0);
  const remainingCount = Number(promo?.remaining_count || 0);

  const usedCount = useMemo(() => {
    if (!promo) {
      return 0;
    }
    const raw = promo.used_count ?? promo.redeemed_count;
    if (raw !== undefined && raw !== null && raw !== "") {
      const n = Number(raw);
      return Number.isFinite(n) ? Math.max(0, n) : 0;
    }
    const max = Number(promo.max_count || 0);
    const remaining = Number(promo.remaining_count ?? 0);
    if (max > 0) {
      return Math.max(0, max - remaining);
    }
    return 0;
  }, [promo]);

  const usagePercent = useMemo(() => {
    if (!maxCount) {
      return 0;
    }
    return Math.min((usedCount / maxCount) * 100, 100);
  }, [maxCount, usedCount]);

  const durationLabel = useMemo(() => {
    if (!plan) {
      return "";
    }

    const months = Number(plan.months || 0);
    const days = Number(plan.days || 0);

    if (months > 0 && days > 0) {
      return fillPlaceholders(data.promoAccessCodeMonthsAndDays, { months, days });
    }

    if (months > 0) {
      if (months === 1) {
        return data.promoAccessCodeOneMonth || "1 сарын эрхтэй код";
      }
      return fillPlaceholders(data.promoAccessCodeMonths, { months });
    }

    if (days > 0) {
      if (days === 1) {
        return data.promoAccessCodeOneDay || "1 өдрийн эрхтэй код";
      }
      return fillPlaceholders(data.promoAccessCodeDays, { days });
    }

    return data.promoAccessCodeUnknown || "-";
  }, [data, plan]);

  const usedRedemptionsText = useMemo(() => {
    const withMax = maxCount > 0;
    const tpl = withMax ? data.promoRedemptionsUsed : data.promoRedemptionsUsedNoMax;
    const template = tpl || "{used} эрх ашиглагдсан байна";
    const values = { used: usedCount, ...(withMax ? { max: maxCount } : {}) };
    return fillPlaceholders(template, values);
  }, [data, maxCount, usedCount]);

  const remainingOfferText = (data.onlyRedemptionsRemaining || "").replace("{count}", String(remainingCount));

  const shareUrl = typeof window !== "undefined" ? window.location.href : "";

  const promoSteps = useMemo(
    () => [
      {
        image: "/images/promo/promo1.png",
        title: data.promoStepOneTitle || "Open promo code",
        description: data.promoStepOneDescription || "Open your profile and choose the promo code option.",
      },
      {
        image: "/images/promo/promo2.png",
        title: data.promoStepTwoTitle || "Enter the code",
        description: data.promoStepTwoDescription || "Enter the promo code shown above and submit it in the app.",
      },
    ],
    [data]
  );

  const handleShare = async () => {
    setActionErrorMsg("");
    setSuccessMsg("");

    const sharePayload = {
      title: data.shareTitle,
      text: data.shareText,
      url: shareUrl,
    };

    try {
      if (navigator.share) {
        await navigator.share(sharePayload);
        setSuccessMsg(data.shareSuccess);
        return;
      }

      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(shareUrl);
        setSuccessMsg(data.shareSuccess);
        return;
      }

      setActionErrorMsg(data.shareUnavailable);
    } catch (error) {
      if (error?.name === "AbortError") {
        return;
      }

      setActionErrorMsg(data.shareUnavailable);
    }
  };

  const handleCopyPromoCode = async () => {
    setActionErrorMsg("");
    setSuccessMsg("");

    try {
      const copied = await copyTextToClipboard(code);
      if (copied) {
        setSuccessMsg(data.copyPromoCodeSuccess || "Promo code copied.");
        return;
      }

      setActionErrorMsg(data.copyPromoCodeUnavailable || data.shareUnavailable);
    } catch (error) {
      setActionErrorMsg(data.copyPromoCodeUnavailable || data.shareUnavailable);
    }
  };

  return (
    <div className="promo-code-public">
      <div className="promo-code-public__inner">
        {loading ? (
          <div className="promo-code-public__loading">
            <AiOutlineLoading3Quarters size={28} className="loading-spinner" />
            <div>{data.loading}</div>
          </div>
        ) : (
          <div className="promo-code-public__card">
            {errorMsg ? (
              <div className="promo-code-public__state">
                <div className="promo-code-public__eyebrow">{data.code}</div>
                <h1>{data.appTitle}</h1>
                <p>{errorMsg}</p>
              </div>
            ) : (
              <>
                <div className="promo-code-public__app-row">
                  <img src="/images/icon.jpg" alt={data.appTitle} className="promo-code-public__app-logo" />
                  <h1>{data.planName}</h1>
                </div>

                <div className="promo-code-public__plan">
                  <div className="promo-code-public__promo-code">{durationLabel}</div>
                  <div className="promo-code-public__code-row">
                    <h2>{code}</h2>
                    <button
                      type="button"
                      className="promo-code-public__copy-button"
                      onClick={handleCopyPromoCode}
                    >
                      <AiOutlineCopy size={16} aria-hidden="true" />
                      {data.copyPromoCode || "Copy code"}
                    </button>
                  </div>
                </div>

                <div className="promo-code-public__availability">
                  <div className="promo-code-public__usage-line">{usedRedemptionsText}</div>
                  <div className="promo-code-public__progress">
                    <div className="promo-code-public__progress-fill" style={{ width: `${usagePercent}%` }} />
                  </div>
                  <div className="promo-code-public__remaining-text">{remainingOfferText}</div>
                </div>

                {successMsg ? <div className="success-msg">{successMsg}</div> : null}
                {actionErrorMsg ? <div className="error-msg">{actionErrorMsg}</div> : null}

                <div className="promo-code-public__actions">
                  <button
                    className="promo-code-public__button promo-code-public__button--secondary"
                    onClick={handleShare}
                  >
                    {data.shareWithFriends}
                  </button>
                </div>

                {/* <div className="promo-code-public__hint">{isUnavailable ? data.statusSoldOut : data.redeemHint}</div> */}

                <section className="promo-code-public__steps" aria-labelledby="promo-code-public-steps-title">
                  <h3 id="promo-code-public-steps-title">
                    {data.promoStepsTitle || "How to enter your promo code"}
                  </h3>
                  <div className="promo-code-public__steps-list">
                    {promoSteps.map((step, index) => (
                      <figure className="promo-code-public__step" key={step.image}>
                        <div className="promo-code-public__step-image-wrap">
                          <img
                            src={step.image}
                            alt={`${step.title} screenshot`}
                            className="promo-code-public__step-image"
                          />
                        </div>
                        <figcaption>
                          <span>{index + 1}</span>
                          <div>
                            <strong>{step.title}</strong>
                            <p>{step.description}</p>
                          </div>
                        </figcaption>
                      </figure>
                    ))}
                  </div>
                </section>
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default PromoCodePublic;
